import { useCallback, useMemo, useState } from 'react';

import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTypeaheadMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.constants';
import { AIOnboardingModalFormShape } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { useAIOnboardingModalContext } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';
import { FetchAiOnboardingModalDataV2Document } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModalV2.generated';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';
import { InsurancePolicyTypeCopyMap } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { InsurancePolicyKind } from '@/types/schema';
import { PathsOf } from '@/types/subform';

interface LifeInsurancePolicyFieldsRowProps {
  index: number;
}

export function LifeInsurancePolicyFieldsRow({
  index,
}: LifeInsurancePolicyFieldsRowProps) {
  const { entities, primaryClients } = useHouseholdDetailsContext();
  const {
    household: { id: householdId },
  } = useAIOnboardingModalContext();

  const { control, setValue } = useFormContext<AIOnboardingModalFormShape>();

  const { styles, columns } =
    AI_ONBOARDING_MODAL_FORM_TABLE_STYLE_CONFIGS.lifeInsurancePolicies;

  const entitySelectOptions = useMemo(
    () =>
      (entities || []).map((entity) => ({
        value: entity.id,
        display: entity.subtype.displayName,
      })),
    [entities]
  );

  const policyTypeOptions = useMemo(
    () =>
      Object.values(InsurancePolicyKind).map((type) => ({
        value: type,
        display: InsurancePolicyTypeCopyMap[type],
      })),
    []
  );

  const insuredSelectOptions = useMemo(
    () =>
      (primaryClients || []).map((pc) => ({
        value: pc.id,
        display: pc.displayName,
      })),
    [primaryClients]
  );

  const [isEntityModalOpen, setEntityModalOpen] = useState(false);
  const [policyHolderInputValue, setPolicyHolderInputValue] = useState('');

  const handleAfterCreateEntity = useCallback(
    (newEntityId: string) => {
      setValue(
        `lifeInsurancePolicies.${index}.ownerId` as const satisfies PathsOf<AIOnboardingModalFormShape>,
        newEntityId
      );
      setEntityModalOpen(false);
    },
    [index, setValue]
  );

  return (
    <>
      {isEntityModalOpen && (
        <CreateEntityShortFormModal
          isOpen={isEntityModalOpen}
          onClose={() => setEntityModalOpen(false)}
          householdId={householdId}
          isOnboardingFlow={false}
          onAfterCreate={handleAfterCreateEntity}
          ignoredQueryDocuments={[FetchAiOnboardingModalDataV2Document]}
        />
      )}
      <FormLayoutRow sx={{ gap: styles.columnPaddingHorizontal }}>
        <FormLayoutItem width={columns.policyType.columnWidth}>
          <FormAwareSelectInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `lifeInsurancePolicies.${index}.policyType` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.policyType.columnHeader}
            options={policyTypeOptions}
            hideLabel
            required
            showErrorBeforeSubmit
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.ownerId.columnWidth}>
          <FormAwareTypeaheadSelectInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `lifeInsurancePolicies.${index}.ownerId` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.ownerId.columnHeader}
            options={entitySelectOptions}
            hideLabel
            required
            showErrorBeforeSubmit
            addNewOption={{
              onClick: () => setEntityModalOpen(true),
              text: 'Add entity',
            }}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow
        sx={{
          marginTop: -2,
          gap: styles.columnPaddingHorizontal,
          backgroundColor: COLORS.GRAY['100'],
          padding: 1,
          borderRadius: 0.75,
        }}
      >
        <FormLayoutItem width={columns.deathBenefitAmount.columnWidth}>
          <FormAwareCurrencyInput<AIOnboardingModalFormShape>
            isDecimalJSInput
            control={control}
            fieldName={
              `lifeInsurancePolicies.${index}.deathBenefitAmount` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.deathBenefitAmount.columnSubheader ?? ''}
          />
        </FormLayoutItem>
        <FormLayoutItem width={columns.policyHolderIDs.columnWidth}>
          <FormAwareTypeaheadMultiSelectInput<AIOnboardingModalFormShape>
            control={control}
            fieldName={
              `lifeInsurancePolicies.${index}.policyHolderIDs` as const satisfies PathsOf<AIOnboardingModalFormShape>
            }
            label={columns.policyHolderIDs.columnSubheader ?? ''}
            options={insuredSelectOptions}
            inputValue={policyHolderInputValue}
            onInputChange={setPolicyHolderInputValue}
            required
            showErrorBeforeSubmit
          />
        </FormLayoutItem>
      </FormLayoutRow>
    </>
  );
}
